<template>
  <div class="monthly-savings-card card h-100">
    <!-- Card Header -->
    <div class="card-header">
      <div class="header-icon">
        <i class="bi bi-graph-up"></i>
      </div>
      <div class="header-content text-start">
        <h3 class="card-title">Monthly Savings</h3>
        <p class="card-subtitle">Your progress this month</p>
      </div>
      <button @click="showModal = true" class="expand-btn d-none d-md-flex" title="Expand chart">
        <i class="fa-solid fa-expand"></i>
      </button>
    </div>

    <div class="card-body">
      <div class="chart-container">
        <canvas ref="chartCanvas" width="400" height="200"></canvas>
      </div>

      <div class="mt-2 small text-muted text-center">
        {{ isViewingCurrentMonth ? 'Your savings journey for' : 'Final savings for' }} {{ currentMonthName }}
      </div>
    </div>

    <!-- Full Screen Modal -->
    <div v-if="showModal" class="modal-overlay" @click="closeModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <div class="modal-title-section">
            <div class="modal-icon">
              <i class="bi bi-graph-up"></i>
            </div>
            <div>
              <h2 class="modal-title">Monthly Savings Chart</h2>
              <p class="modal-subtitle">{{ currentMonthName }} - {{ isViewingCurrentMonth ? 'Detailed View with Goals' : 'Complete Month View' }}</p>
            </div>
          </div>
          <button @click="closeModal" class="modal-close-btn">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>
        
        <div class="modal-body">
          <div class="expanded-chart-container">
            <canvas ref="expandedChartCanvas" width="800" height="400"></canvas>
          </div>
          
          <div class="chart-stats">
            <div class="stat-item">
              <div class="stat-label">
                <i class="fa-solid fa-piggy-bank me-1"></i>
                Total Saved This Month
              </div>
              <div class="stat-value" :class="{ 'negative': totalSavedThisMonth < 0 }">
                ${{ totalSavedThisMonth.toFixed(2) }}
              </div>
            </div>
            <div class="stat-item">
              <div class="stat-label">
                <i class="fa-solid fa-chart-line me-1"></i>
                Projected Month-End
              </div>
              <div class="stat-value" :class="{ 'negative': projectedEndMonthSavings < 0 }">
                ${{ projectedEndMonthSavings.toFixed(2) }}
              </div>
            </div>
            <div class="stat-item">
              <div class="stat-label">
                <i class="fa-solid fa-calendar-day me-1"></i>
                Typical Daily Savings
              </div>
              <div class="stat-value" :class="{ 'negative': dailySavingsData.averageDailySaved < 0 }">
                ${{ dailySavingsData.averageDailySaved.toFixed(2) }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Chart from 'chart.js/auto';
import { shallowRef, watch } from 'vue';
import { useBudgetData } from '../composables/useBudgetData';
import { useSavingsCalculations } from '../composables/useSavingsCalculations';
import { useDarkMode } from '../composables/useDarkMode';

export default {
  name: 'MonthlySavingsChart',
  setup() {
    const { currentDate, daysInMonth, isViewingCurrentMonth } = useBudgetData()
    const { dailySavingsData, totalSavedThisMonth, projectedEndMonthSavings } = useSavingsCalculations()
    const { isDarkMode, currentTheme } = useDarkMode()
    
    return {
      currentDate,
      daysInMonth,
      dailySavingsData,
      totalSavedThisMonth,
      projectedEndMonthSavings,
      isDarkMode,
      currentTheme,
      isViewingCurrentMonth
    }
  },
  data() {
    return {
      chart: shallowRef(null),
      expandedChart: shallowRef(null),
      showModal: false
    };
  },
  computed: {
    currentMonthName() {
      return this.currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    },

    chartData() {
      const data = this.dailySavingsData;
      const showProjection = this.isViewingCurrentMonth;
      // Force recalculation when theme changes by accessing currentTheme
      const primaryColor = this.currentTheme && this.getPrimaryColor();

      const datasets = [
        {
          label: 'Your Savings So Far',
          data: [...data.runningSavings, ...Array(data.allLabels.length - data.runningSavings.length).fill(null)],
          borderColor: primaryColor,
          backgroundColor: (context) => {
            const chart = context.chart;
            const { ctx, chartArea } = chart;

            if (!chartArea) {
              return null;
            }

            return this.createGradient(ctx, chartArea, chart);
          },
          segment: {
            borderColor: primaryColor
          },
          borderWidth: 3,
          borderJoinStyle: 'round',
          borderCapStyle: 'round',
          fill: true,
          stepped: true,
          pointBackgroundColor: primaryColor,
          pointBorderColor: '#ffffff',
          pointBorderWidth: 0,
          pointRadius: 0,
          pointHoverRadius: 0,
          spanGaps: false
        }
      ];

      // Only add projection dataset if viewing current month
      if (showProjection) {
        datasets.push({
          label: 'Projected Path',
          data: [...Array(data.labels.length - 1).fill(null), ...data.projectedSavings],
          borderColor: data.projectedSavings.length > 0 && data.projectedSavings[data.projectedSavings.length - 1] < 0 ? '#dc3545' : primaryColor,
          backgroundColor: 'transparent',
          borderWidth: 2,
          borderDash: [6, 8],
          borderJoinStyle: 'round',
          borderCapStyle: 'round',
          fill: false,
          stepped: false,
          pointBackgroundColor: data.projectedSavings.length > 0 && data.projectedSavings[data.projectedSavings.length - 1] < 0 ? '#dc3545' : primaryColor,
          pointBorderColor: '#ffffff',
          pointBorderWidth: 0,
          pointRadius: 0,
          pointHoverRadius: 0,
          spanGaps: false,
        });
      }

      return {
        labels: data.allLabels,
        datasets
      };
    }
  },
  watch: {
    dailySavingsData: {
      handler() {
        this.updateChartData();
        if (this.expandedChart) {
          this.updateExpandedChartData();
        }
      },
      deep: true
    },
    currentTheme() {
      // Update charts when theme changes
      this.$nextTick(() => {
        this.updateChart();
        if (this.expandedChart) {
          this.updateExpandedChart();
        }
      });
    },
    showModal(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          this.createExpandedChart();
        });
      } else if (this.expandedChart) {
        this.expandedChart.destroy();
        this.expandedChart = shallowRef(null);
      }
    }
  },
  mounted() {
    this.createChart();
  },
  beforeUnmount() {
    if (this.chart) {
      this.chart.destroy();
    }
    if (this.expandedChart) {
      this.expandedChart.destroy();
    }
  },
  methods: {
    getCSSVariableValue(variableName) {
      // Read from body element since data-theme attribute is applied there
      const element = document.body.hasAttribute('data-theme') ? document.body : document.documentElement;
      const value = getComputedStyle(element).getPropertyValue(variableName).trim();
      return value;
    },

    getPrimaryColor() {
      return this.getCSSVariableValue('--btn-primary-color');
    },

    createChart() {
      const ctx = this.$refs.chartCanvas.getContext('2d');
      this.chart = shallowRef(this.createChartInstance(ctx, false));
    },

    createExpandedChart() {
      if (!this.$refs.expandedChartCanvas) return;
      const ctx = this.$refs.expandedChartCanvas.getContext('2d');
      this.expandedChart = shallowRef(this.createChartInstance(ctx, true));
    },

    createChartInstance(ctx, isExpanded = false) {
      return new Chart(ctx, {
        type: 'line',
        data: this.chartData,
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: isExpanded,
              position: 'top',
              labels: {
                color: this.getCSSVariableValue('--light-text'),
                usePointStyle: true,
                padding: 20,
                font: {
                  size: 14
                },
                generateLabels: (chart) => {
                  const datasets = chart.data.datasets;
                  const textColor = this.getCSSVariableValue('--light-text');
                  const primaryColor = this.getPrimaryColor();
                  
                  return datasets.map((dataset, index) => {
                    let color = primaryColor; // Default primary color
                    
                    if (index === 0) {
                      // Your Savings So Far - always primary color
                      color = primaryColor;
                    } else if (index === 1) {
                      // Projected Path - primary color or red based on final value
                      const data = this.dailySavingsData;
                      color = data.projectedSavings.length > 0 && data.projectedSavings[data.projectedSavings.length - 1] < 0 ? '#dc3545' : primaryColor;
                    }
                    
                    return {
                      text: dataset.label,
                      fillStyle: color,
                      strokeStyle: color,
                      fontColor: textColor,
                      lineWidth: index === 0 ? 3 : 2,
                      lineDash: index === 1 ? [6, 8] : [],
                      pointStyle: 'line'
                    };
                  });
                }
              }
            },
            tooltip: {
              mode: 'index',
              intersect: false,
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              titleColor: '#ffffff',
              bodyColor: '#ffffff',
              borderColor: this.getPrimaryColor(),
              borderWidth: 1,
              titleFont: {
                size: isExpanded ? 14 : 12
              },
              bodyFont: {
                size: isExpanded ? 13 : 11
              },
              callbacks: {
                title: (context) => {
                  return `Day ${context[0].label}`;
                },
                label: (context) => {
                  if (context.datasetIndex === 0) {
                    return `So Far: $${context.parsed.y.toFixed(2)}`;
                  } else {
                    return `Projected: $${context.parsed.y.toFixed(2)}`;
                  }
                },
                footer: (context) => {
                  if (context.length > 1 && context[1].datasetIndex === 1) {
                    const data = this.dailySavingsData;
                    return `Avg daily: $${data.averageDailySaved.toFixed(2)}`;
                  }
                  return '';
                }
              }
            }
          },
          scales: {
            x: {
              title: {
                display: true,
                text: 'Day of Month',
                color: this.getCSSVariableValue('--light-text-muted'),
                font: {
                  size: isExpanded ? 14 : 12
                }
              },
              grid: {
                color: this.getCSSVariableValue('--light-border')
              },
              ticks: {
                color: this.getCSSVariableValue('--light-text-muted'),
                font: {
                  size: isExpanded ? 12 : 10
                }
              }
            },
            y: {
              title: {
                display: true,
                text: 'Total Saved ($)',
                color: this.getCSSVariableValue('--light-text-muted'),
                font: {
                  size: isExpanded ? 14 : 12
                }
              },
              grid: {
                color: this.getCSSVariableValue('--light-border')
              },
              ticks: {
                color: this.getCSSVariableValue('--light-text-muted'),
                font: {
                  size: isExpanded ? 12 : 10
                },
                callback: function (value) {
                  return '$' + value.toFixed(0);
                }
              },
              afterDataLimits: (scale) => {
                if (scale.min > 0) {
                  scale.min = 0;
                }
                if (scale.max < 0) {
                  scale.max = 0;
                }
              }
            }
          },
          interaction: {
            mode: 'nearest',
            axis: 'x',
            intersect: false
          },
          elements: {
            point: {
              hoverBackgroundColor: this.getPrimaryColor(),
              radius: isExpanded ? 4 : 0,
              hoverRadius: isExpanded ? 6 : 0
            }
          }
        }
      });
    },

    updateChart() {
      if (this.chart) {
        console.log('go');
        // Destroy and recreate chart to update theme colors
        this.chart.destroy();
        this.createChart();
      }
    },

    updateChartData() {
      if (this.chart) {
        // Just update data without recreating chart
        this.chart.data = this.chartData;
        this.chart.update('none');
      }
    },

    updateExpandedChart() {
      if (this.expandedChart) {
        // Destroy and recreate expanded chart to update theme colors
        this.expandedChart.destroy();
        this.createExpandedChart();
      }
    },

    updateExpandedChartData() {
      if (this.expandedChart) {
        // Just update data without recreating chart
        this.expandedChart.data = this.chartData;
        this.expandedChart.update('none');
      }
    },

    closeModal() {
      this.showModal = false;
    },

    createGradient(ctx, chartArea, chart) {
      const { top, bottom } = chartArea;
      const yScale = chart.scales.y;

      // Calculate where zero is positioned on the chart
      const zeroPixel = yScale.getPixelForValue(0);

      // Create gradient
      const gradient = ctx.createLinearGradient(0, top, 0, bottom);

      // Calculate the position of zero as a percentage of the chart height
      const zeroPosition = Math.max(0, Math.min(1, (zeroPixel - top) / (bottom - top)));

      // Get primary color and convert to rgba
      const primaryColor = this.getPrimaryColor();
      const primaryRgba = this.hexToRgba(primaryColor, 0.3);
      const primaryRgbaLight = this.hexToRgba(primaryColor, 0);

      // Add color stops - primary color above zero, red below zero
      if (zeroPosition > 0) {
        gradient.addColorStop(0, primaryRgba); // Primary color at top
        gradient.addColorStop(zeroPosition, primaryRgbaLight); // Light primary approaching zero from above
      }

      if (zeroPosition < 1) {
        gradient.addColorStop(zeroPosition, 'rgba(220, 53, 69, 0)'); // Light red starting from zero going down
        gradient.addColorStop(1, 'rgba(220, 53, 69, 0.3)'); // Red at bottom
      }

      return gradient;
    },

    hexToRgba(hex, alpha) {
      // Remove # if present
      hex = hex.replace('#', '');
      
      // Parse hex values
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }
  }
};
</script>

<style scoped>
.monthly-savings-card:hover {
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.header-content {
  flex: 1;
  text-align: left;
}

/* On mobile, when expand button is hidden, align content to start */
@media (max-width: 768px) {
  .card-header {
    justify-content: flex-start;
  }
}

.expand-btn {
  background: none;
  border: none;
  color: var(--light-text-muted);
  font-size: 1.1rem;
  padding: 0.5rem;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
}

.expand-btn:hover {
  color: var(--light-text);
  background: var(--light-border);
}

.chart-container {
  position: relative;
  height: 250px;
  width: 100%;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
  padding: 1rem;
}

.modal-content {
  background: var(--light-card);
  border-radius: 12px;
  width: 100%;
  max-width: 1200px;
  max-height: 90vh;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem 2rem;
  border-bottom: 1px solid var(--light-border);
  background: var(--nested-container-bg);
}

.modal-title-section {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.modal-icon {
  width: 50px;
  height: 50px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #36A2EB, #4BC0C0);
  color: white;
  font-size: 1.5rem;
}

.modal-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--light-text);
  margin: 0;
}

.modal-subtitle {
  font-size: 0.95rem;
  color: var(--light-text-muted);
  margin: 0.25rem 0 0 0;
}

.modal-close-btn {
  background: none;
  border: none;
  color: var(--light-text-muted);
  font-size: 1.25rem;
  padding: 0.5rem;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
}

.modal-close-btn:hover {
  color: var(--light-text);
  background: var(--light-border);
}

.modal-body {
  padding: 2rem;
  flex: 1;
  overflow-y: auto;
}

.expanded-chart-container {
  position: relative;
  height: 500px;
  width: 100%;
  margin-bottom: 2rem;
}

.chart-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-top: 1rem;
}

.stat-item {
  background: var(--nested-container-bg);
  border-radius: 8px;
  padding: 1.5rem;
  border: 2px dashed var(--light-border);
  transition: all 0.2s ease;
}

.stat-item:hover {
  border-color: var(--light-text);
}

.stat-label {
  font-size: 0.95rem;
  color: var(--light-text);
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
  font-weight: 500;
}

.stat-value {
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--light-success);
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
}

.stat-value.negative {
  color: var(--light-danger);
}

.metric-value {
  text-align: right;
}

.metric-value .value {
  font-size: 1.2rem;
  font-weight: 600;
  color: #28a745;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
}

.metric-value .value.negative {
  color: #dc3545;
}

.projected-value {
  text-align: right;
}

.projected-amount {
  font-size: 1.2rem;
  font-weight: 600;
  color: #1364a3;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
}

.projected-amount.negative {
  color: #dc3545;
}

/* Dark Mode Support */
:global([data-theme]) .modal-overlay {
  background: rgba(0, 0, 0, 0.9);
}

:global([data-theme]) .modal-content {
  background: var(--light-card);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
}

/* Responsive Design */
@media (max-width: 768px) {
  .modal-overlay {
    padding: 0.5rem;
  }

  .modal-header {
    padding: 1rem 1.5rem;
  }

  .modal-body {
    padding: 1.5rem;
  }

  .expanded-chart-container {
    height: 350px;
  }

  .chart-stats {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .stat-item {
    padding: 1.25rem;
  }

  .stat-value {
    font-size: 1.5rem;
  }

  .modal-title {
    font-size: 1.25rem;
  }

  .modal-icon {
    width: 40px;
    height: 40px;
    font-size: 1.25rem;
  }
}
</style>